import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Edit,
  Trash2,
  Phone,
  MessageSquare,
  FileText,
  Calendar,
  CheckCircle,
  User,
  Building,
  TrendingUp,
  Plus,
  Save,
  Upload,
  Download,
  Eye,
  MoreVertical,
  Target,
  Users,
  Wallet,
  CreditCard,
  Clock,
  CheckCircle2,
  AlertCircle,
  IndianRupee
} from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Lead } from '@/types/lead';
import { LeadActivity, Task, getLeadPayments, recordPayment, getLeadMilestones } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ReassignLeadModal } from './ReassignLeadModal';
import { EditLeadModal } from './EditLeadModal';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  Payment,
  PaymentMilestone,
  PaymentMethod,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_STATUS_LABELS,
  PaymentStatus,
  CreatePaymentData
} from '@/types/payment';
import { DispositionHistoryTimeline } from './leads/DispositionHistoryTimeline';
import { UnifiedLeadTimeline } from './leads/UnifiedLeadTimeline';
import { SubscriptionInfo } from './subscription/SubscriptionInfo';

interface LeadDetailPanelProps {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
  onLeadUpdate: (lead: Lead) => void;
  initialTab?: TabType;
}

type TabType = 'info' | 'timeline' | 'tasks' | 'notes' | 'files' | 'payments' | 'interaction';

const SUB_DISPOSITIONS = [
  'Callback Requested',
  'Not Interested',
  'Already Joined',
  'Price Too High',
  'Need More Info',
  'Wrong Number',
  'No Answer',
  'Busy',
  'Follow Up Later',
  'Interested - Hot',
  'Interested - Warm',
];

const NEEDS_FOLLOWUP = ['Callback Requested', 'Follow Up Later', 'Interested - Hot', 'Interested - Warm', 'Need More Info'];

export const LeadDetailPanel: React.FC<LeadDetailPanelProps> = ({
  lead,
  isOpen,
  onClose,
  onLeadUpdate,
  initialTab,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab || 'info');
  const [isEditing, setIsEditing] = useState(false);
  const [editedLead, setEditedLead] = useState<Lead | null>(null);
  const [newNote, setNewNote] = useState('');
  const [newTask, setNewTask] = useState({ title: '', description: '', dueDate: '', priority: 'MEDIUM' as const });
  const [newActivity, setNewActivity] = useState({ type: 'NOTE' as const, content: '' });
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [interaction, setInteraction] = useState({ subDisposition: '', followUpDate: '', followUpNote: '', createTask: false, taskTitle: '', taskPriority: 'MEDIUM' });
  const [isLoggingInteraction, setIsLoggingInteraction] = useState(false);
  const [newPayment, setNewPayment] = useState<CreatePaymentData>({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'BANK_TRANSFER' as PaymentMethod,
    notes: ''
  });

  // Fetch full lead data with activities when panel opens
  const { data: fullLeadData, isLoading: isLoadingFullLead } = useQuery({
    queryKey: ['lead-detail', lead?.id],
    queryFn: async () => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/${lead!.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      return data.success ? data.data : data;
    },
    enabled: isOpen && !!lead?.id,
    staleTime: 0,
  });

  // Fetch payments for WON leads
  const { data: paymentsData, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['lead-payments', lead?.id],
    queryFn: () => getLeadPayments(lead!.id),
    enabled: isOpen && !!lead?.id && lead?.stage === 'WON',
  });

  // Record payment mutation
  const recordPaymentMutation = useMutation({
    mutationFn: (data: CreatePaymentData) => recordPayment(lead!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-payments', lead?.id] });
      queryClient.invalidateQueries({ queryKey: ['lead-detail', lead?.id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowAddPayment(false);
      setNewPayment({
        amount: 0,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'BANK_TRANSFER' as PaymentMethod,
        notes: ''
      });
      toast({
        title: "Payment recorded",
        description: "Payment has been recorded successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to record payment",
        description: error.message || "Something went wrong",
        variant: "destructive",
      });
    }
  });

  // Use full lead data when available
  const displayLead = fullLeadData || lead;

  useEffect(() => {
    if (lead) {
      setEditedLead(lead);
    }
  }, [lead]);

  const handleSave = () => {
    if (editedLead) {
      onLeadUpdate(editedLead);
      setIsEditing(false);
    }
  };

  // Handlers for adding activities, notes, and tasks
  const handleAddActivity = async () => {
    if (!lead || !newActivity.content.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/${lead.id}/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: newActivity.type,
          content: newActivity.content,
          createdBy: 'current-user'
        })
      });

      if (response.ok) {
        setNewActivity({ type: 'NOTE', content: '' });
        queryClient.invalidateQueries({ queryKey: ['lead-detail', lead.id] });
        // Also refresh the unified timeline which reads from a different query key
        queryClient.invalidateQueries({ queryKey: ['lead-timeline', lead.id] });
        if (lead) {
          onLeadUpdate(lead);
        }
        toast({ title: "Activity added", description: "Activity has been logged successfully." });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast({ title: "Failed to add activity", description: errorData.message || "Something went wrong", variant: "destructive" });
      }
    } catch (error) {
      console.error('Failed to add activity:', error);
      toast({ title: "Error", description: "Network error. Please check your connection.", variant: "destructive" });
    }
  };

  const handleAddNote = async () => {
    if (!lead || !newNote.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/${lead.id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newNote })
      });

      if (response.ok) {
        setNewNote('');
        toast({
          title: "Note added",
          description: "Your note has been saved successfully.",
        });
        // Trigger refresh of lead data
        queryClient.invalidateQueries({ queryKey: ['lead-detail', lead.id] });
        if (lead) {
          onLeadUpdate(lead);
        }
      } else {
        // Handle error responses
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = response.status === 403
          ? "You don't have permission to add notes to this lead"
          : errorData.message || errorData.error || 'Failed to add note';

        toast({
          title: "Failed to add note",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Failed to add note:', error);
      toast({
        title: "Error",
        description: "Network error. Please check your connection and try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddTask = async () => {
    if (!lead || !newTask.title.trim() || !newTask.dueDate) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/${lead.id}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newTask.title,
          description: newTask.description,
          dueDate: newTask.dueDate,
          priority: newTask.priority
        })
      });

      if (response.ok) {
        setNewTask({ title: '', description: '', dueDate: '', priority: 'MEDIUM' });
        // Trigger refresh of lead data
        queryClient.invalidateQueries({ queryKey: ['lead-detail', lead.id] });
        if (lead) {
          onLeadUpdate(lead);
        }
      }
    } catch (error) {
      console.error('Failed to add task:', error);
    }
  };

  const handleReassign = async (newOwnerId: string, note?: string) => {
    if (!lead) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/${lead.id}/reassign`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ newOwnerId, note })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reassign lead');
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to reassign lead');
      }

      // Success - update the lead with new data
      if (data.data && data.data.lead) {
        onLeadUpdate(data.data.lead);
      }

      // Close modal
      setShowReassignModal(false);
    } catch (error: any) {
      console.error('Error reassigning lead:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  const getActivityIcon = (type: LeadActivity['type']) => {
    switch (type) {
      case 'CALL': return <Phone className="h-4 w-4" />;
      case 'MESSAGE': return <MessageSquare className="h-4 w-4" />;
      case 'FILE': return <FileText className="h-4 w-4" />;
      case 'NOTE': return <FileText className="h-4 w-4" />;
      case 'STATUS_CHANGE': return <CheckCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getTaskPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-700 border-green-200';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'PENDING': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getFileIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf': return <FileText className="h-5 w-5 text-red-500" />;
      case 'doc':
      case 'docx': return <FileText className="h-5 w-5 text-blue-500" />;
      case 'xls':
      case 'xlsx': return <FileText className="h-5 w-5 text-green-500" />;
      case 'ppt':
      case 'pptx': return <FileText className="h-5 w-5 text-orange-500" />;
      case 'zip':
      case 'rar': return <FileText className="h-5 w-5 text-purple-500" />;
      case 'mp4':
      case 'avi':
      case 'mov': return <FileText className="h-5 w-5 text-blue-500" />;
      default: return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const getDaysInPipeline = () => {
    if (!displayLead?.createdAt) return 0;
    const created = new Date(displayLead.createdAt);
    if (isNaN(created.getTime())) return 0;
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'HOT': return 'bg-red-100 text-red-700 border-red-200';
      case 'WARM': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'COLD': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (!lead || !editedLead) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="lead-detail-panel"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={`Lead details for ${lead?.name || 'lead'}`}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
          onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-4xl h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <Card className="bg-white shadow-2xl h-full flex flex-col overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Building className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{lead.name}</h2>
                      <p className="text-gray-600">{lead.company}</p>
                      <div className="flex items-center space-x-2 mt-2 flex-wrap gap-1">
                        <Badge variant="secondary" className="text-xs">
                          {lead.stage}
                        </Badge>
                        <Badge className={`text-xs ${getStatusColor(lead.status ?? 'WARM')}`}>
                          {lead.status ?? 'WARM'}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          ₹{(lead.value ?? 0).toLocaleString()}
                        </Badge>
                        {lead.source && (
                          <Badge
                            className="text-xs"
                            style={{
                              backgroundColor: `${lead.source.color}20`,
                              color: lead.source.color,
                              borderColor: lead.source.color
                            }}
                          >
                            <Target className="h-3 w-3 mr-1" />
                            {lead.source.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEditModal(true)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Lead
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 flex-shrink-0">
                <div className="flex space-x-8 px-6">
                  {(['info', 'timeline', 'tasks', 'notes', 'files'] as TabType[]).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                        activeTab === tab
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                  {/* Payments tab - available for all stages */}
                  {(
                    <button
                      onClick={() => setActiveTab('payments')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                        activeTab === 'payments'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Wallet className="h-4 w-4" />
                      Payments
                      {paymentsData?.summary && (
                        <Badge
                          className={`ml-1 text-xs ${PAYMENT_STATUS_COLORS[paymentsData.summary.paymentStatus as PaymentStatus]?.bg || 'bg-gray-100'} ${PAYMENT_STATUS_COLORS[paymentsData.summary.paymentStatus as PaymentStatus]?.text || 'text-gray-700'}`}
                        >
                          {PAYMENT_STATUS_LABELS[paymentsData.summary.paymentStatus as PaymentStatus] || paymentsData.summary.paymentStatus}
                        </Badge>
                      )}
                    </button>
                  )}
                  {/* Log Interaction tab */}
                  <button
                    onClick={() => setActiveTab('interaction')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-1 whitespace-nowrap ${
                      activeTab === 'interaction'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Phone className="h-4 w-4" />
                    Log Call
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <AnimatePresence mode="wait">
                  {activeTab === 'info' && (
                    <motion.div
                      key="info"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="grid grid-cols-1 md:grid-cols-2 gap-6"
                    >
                      {/* Company Information */}
                      <Card className="p-4">
                        <div className="flex items-center space-x-2 mb-4">
                          <Building className="h-5 w-5 text-gray-500" />
                          <h3 className="font-semibold text-gray-900">Company Information</h3>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Company Name</label>
                            <p className="text-gray-900">{lead.company}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Industry</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Company Size</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Location</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                        </div>
                      </Card>

                      {/* Timeline Information */}
                      <Card className="p-4">
                        <div className="flex items-center space-x-2 mb-4">
                          <Calendar className="h-5 w-5 text-gray-500" />
                          <h3 className="font-semibold text-gray-900">Timeline Information</h3>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Created</label>
                            <p className="text-gray-900">{displayLead.createdAt ? new Date(displayLead.createdAt).toLocaleDateString() : 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Last Updated</label>
                            <p className="text-gray-900">{displayLead.updatedAt ? new Date(displayLead.updatedAt).toLocaleDateString() : 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Last Activity</label>
                            <p className="text-gray-900">{(displayLead.lastActivity || displayLead.createdAt) ? new Date(displayLead.lastActivity || displayLead.createdAt).toLocaleDateString() : 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Days in Pipeline</label>
                            <p className="text-gray-900">{getDaysInPipeline()} days</p>
                          </div>
                        </div>
                      </Card>

                      {/* Contact Information / Owner */}
                      <Card className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <User className="h-5 w-5 text-gray-500" />
                            <h3 className="font-semibold text-gray-900">Lead Owner</h3>
                          </div>
                          {(user?.role === 'CEO' || user?.role === 'MANAGER') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowReassignModal(true)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Users className="h-4 w-4 mr-1" />
                              Change Owner
                            </Button>
                          )}
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-medium">
                                {lead.assignedToUser?.name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {lead.assignedToUser?.name || 'Unassigned'}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {lead.assignedToUser?.email || 'No owner assigned'}
                              </p>
                              {lead.assignedToUser?.role && (
                                <Badge className="text-xs mt-1" variant="outline">
                                  {lead.assignedToUser.role.replace('_', ' ')}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Email</label>
                            <p className="text-gray-900">{lead.assignedToUser?.email || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Phone</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">LinkedIn</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                        </div>
                      </Card>

                      {/* Deal Information */}
                      <Card className="p-4">
                        <div className="flex items-center space-x-2 mb-4">
                          <Target className="h-5 w-5 text-gray-500" />
                          <h3 className="font-semibold text-gray-900">Deal Information</h3>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Deal Value</label>
                            <p className="text-gray-900">₹{(lead.value ?? 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Probability</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Expected Close</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Sales Cycle</label>
                            <p className="text-gray-900">Not specified</p>
                          </div>
                        </div>
                      </Card>

                      {/* Subscription */}
                      <div className="col-span-full mb-4">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2 px-1">
                          Subscription
                        </h3>
                        <SubscriptionInfo leadId={lead.id} />
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'timeline' && (
                    <motion.div
                      key="timeline"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Add Activity */}
                      <Card className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-4">Add Activity</h3>
                        <div className="space-y-4">
                          <div className="flex space-x-4">
                            <select
                              value={newActivity.type}
                              onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value as any })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="NOTE">Note</option>
                              <option value="CALL">Call</option>
                              <option value="MESSAGE">Message</option>
                              <option value="FILE">File</option>
                            </select>
                          </div>
                          <Input
                            placeholder="Activity description..."
                            value={newActivity.content}
                            onChange={(e) => setNewActivity({ ...newActivity, content: e.target.value })}
                            className="w-full"
                          />
                          <Button
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={handleAddActivity}
                            disabled={!newActivity.content.trim()}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Activity
                          </Button>
                        </div>
                      </Card>

                      {/* Unified Timeline — merges activities, invoices, emails */}
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-4">
                          Activity Timeline
                        </h3>
                        {lead?.id ? (
                          <UnifiedLeadTimeline leadId={lead.id} />
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No lead selected</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'tasks' && (
                    <motion.div
                      key="tasks"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Add Task */}
                      <Card className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-4">New Task</h3>
                        <div className="space-y-4">
                          <Input
                            placeholder="Task title"
                            value={newTask.title}
                            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                            className="w-full"
                          />
                          <Input
                            placeholder="Task description (optional)"
                            value={newTask.description}
                            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                            className="w-full"
                          />
                          <div className="flex space-x-4">
                            <Input
                              type="date"
                              value={newTask.dueDate}
                              onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                              className="flex-1"
                            />
                            <select
                              value={newTask.priority}
                              onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="LOW">Low Priority</option>
                              <option value="MEDIUM">Medium Priority</option>
                              <option value="HIGH">High Priority</option>
                            </select>
                          </div>
                          <Button
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={handleAddTask}
                            disabled={!newTask.title.trim() || !newTask.dueDate}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Task
                          </Button>
                        </div>
                      </Card>

                      {/* Tasks List */}
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-4">
                          Tasks ({displayLead?.tasks?.length || 0})
                        </h3>
                        <div className="space-y-4">
                          {displayLead?.tasks && displayLead.tasks.length > 0 ? (
                            displayLead.tasks.map((task: any) => (
                              <Card key={task.id} className="p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">{task.title}</p>
                                    {task.description && (
                                      <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                    )}
                                    <div className="flex items-center space-x-2 mt-2">
                                      <Badge className={getTaskPriorityColor(task.priority)}>
                                        {task.priority}
                                      </Badge>
                                      <Badge className={getTaskStatusColor(task.status)}>
                                        {task.status}
                                      </Badge>
                                      <span className="text-xs text-gray-500">
                                        Due: {new Date(task.dueDate).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                              <p>No tasks yet</p>
                              <p className="text-sm">Create your first task to start managing this lead</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'notes' && (
                    <motion.div
                      key="notes"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Add Note */}
                      <Card className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-4">Add Note</h3>
                        <div className="space-y-4">
                          <Textarea
                            placeholder="Write your note..."
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            className="w-full min-h-[80px]"
                            rows={3}
                          />
                          <Button
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={handleAddNote}
                            disabled={!newNote.trim()}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Note
                          </Button>
                        </div>
                      </Card>

                      {/* Notes List */}
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-4">
                          Notes ({displayLead?.notes?.length || 0})
                        </h3>
                        <div className="space-y-4">
                          {displayLead?.notes && displayLead.notes.length > 0 ? (
                            displayLead.notes.map((note: any) => (
                              <Card key={note.id} className="p-3">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.content}</p>
                                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                  <span className="font-medium text-gray-600">
                                    {note.author?.name || note.author?.email || 'Unknown'}
                                  </span>
                                  <span>•</span>
                                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                                </div>
                              </Card>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                              <p>No notes yet</p>
                              <p className="text-sm">Add your first note to start documenting this lead</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'files' && (
                    <motion.div
                      key="files"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Upload Files */}
                      <Card className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-4">Upload Files</h3>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                          <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                          <p className="text-gray-600 mb-2">Drag and drop files here, or</p>
                          <Button variant="outline">
                            Choose Files
                          </Button>
                        </div>
                      </Card>

                      {/* Files List */}
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-4">Uploaded Files (0)</h3>
                        <div className="space-y-4">
                          <div className="text-center py-8 text-gray-500">
                            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No files uploaded yet</p>
                            <p className="text-sm">Upload files to keep all documents in one place</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'payments' && (
                    <motion.div
                      key="payments"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Payment Summary */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card className="p-4">
                          <div className="flex items-center space-x-2 text-gray-500 mb-1">
                            <IndianRupee className="h-4 w-4" />
                            <span className="text-sm">Deal Value</span>
                          </div>
                          <p className="text-2xl font-bold text-gray-900">
                            {(paymentsData?.summary?.dealValue || lead?.dealValue || 0).toLocaleString('en-IN')}
                          </p>
                        </Card>
                        <Card className="p-4">
                          <div className="flex items-center space-x-2 text-green-500 mb-1">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm">Total Received</span>
                          </div>
                          <p className="text-2xl font-bold text-green-600">
                            {(paymentsData?.summary?.totalPaid || 0).toLocaleString('en-IN')}
                          </p>
                        </Card>
                        <Card className="p-4">
                          <div className="flex items-center space-x-2 text-orange-500 mb-1">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">Pending</span>
                          </div>
                          <p className="text-2xl font-bold text-orange-600">
                            {(paymentsData?.summary?.pendingAmount || (lead?.dealValue || 0) - (paymentsData?.summary?.totalPaid || 0)).toLocaleString('en-IN')}
                          </p>
                        </Card>
                        <Card className="p-4">
                          <div className="flex items-center space-x-2 text-blue-500 mb-1">
                            <Wallet className="h-4 w-4" />
                            <span className="text-sm">Status</span>
                          </div>
                          {paymentsData?.summary?.paymentStatus && (
                            <Badge
                              className={`${PAYMENT_STATUS_COLORS[paymentsData.summary.paymentStatus as PaymentStatus]?.bg || 'bg-gray-100'} ${PAYMENT_STATUS_COLORS[paymentsData.summary.paymentStatus as PaymentStatus]?.text || 'text-gray-700'} text-sm`}
                            >
                              {PAYMENT_STATUS_LABELS[paymentsData.summary.paymentStatus as PaymentStatus] || paymentsData.summary.paymentStatus}
                            </Badge>
                          )}
                        </Card>
                      </div>

                      {/* Add Payment Section */}
                      <Card className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-gray-900">Record Payment</h3>
                          {!showAddPayment && (
                            <Button
                              onClick={() => setShowAddPayment(true)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Payment
                            </Button>
                          )}
                        </div>

                        {showAddPayment && (
                          <div className="space-y-4 border-t pt-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Amount</label>
                                <Input
                                  type="number"
                                  placeholder="Enter amount"
                                  value={newPayment.amount || ''}
                                  onChange={(e) => setNewPayment({ ...newPayment, amount: Number(e.target.value) })}
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Payment Date</label>
                                <Input
                                  type="date"
                                  value={newPayment.paymentDate}
                                  onChange={(e) => setNewPayment({ ...newPayment, paymentDate: e.target.value })}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Payment Method</label>
                                <select
                                  value={newPayment.paymentMethod}
                                  onChange={(e) => setNewPayment({ ...newPayment, paymentMethod: e.target.value as PaymentMethod })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                >
                                  {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Invoice/Receipt #</label>
                                <Input
                                  placeholder="Optional"
                                  value={newPayment.invoiceNumber || ''}
                                  onChange={(e) => setNewPayment({ ...newPayment, invoiceNumber: e.target.value })}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Transaction ID / UTR</label>
                                <Input
                                  placeholder="Optional"
                                  value={newPayment.transactionId || ''}
                                  onChange={(e) => setNewPayment({ ...newPayment, transactionId: e.target.value })}
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Notes</label>
                                <Textarea
                                  placeholder="Payment notes (optional)"
                                  value={newPayment.notes || ''}
                                  onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                                  rows={2}
                                />
                              </div>
                            </div>
                            {(newPayment.paymentMethod === 'BANK_TRANSFER' || newPayment.paymentMethod === 'UPI') && !newPayment.transactionId && (
                              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                Add a transaction ID/UTR for verification
                              </div>
                            )}
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setShowAddPayment(false);
                                  setNewPayment({
                                    amount: 0,
                                    paymentDate: new Date().toISOString().split('T')[0],
                                    paymentMethod: 'BANK_TRANSFER' as PaymentMethod,
                                    notes: ''
                                  });
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => recordPaymentMutation.mutate(newPayment)}
                                disabled={!newPayment.amount || newPayment.amount <= 0 || recordPaymentMutation.isPending}
                              >
                                {recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </Card>

                      {/* Payment History */}
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-4">
                          Payment History ({paymentsData?.payments?.length || 0})
                        </h3>
                        <div className="space-y-3">
                          {isLoadingPayments ? (
                            <div className="text-center py-8">
                              <div className="animate-spin h-8 w-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto"></div>
                              <p className="text-sm text-gray-500 mt-2">Loading payments...</p>
                            </div>
                          ) : paymentsData?.payments && paymentsData.payments.length > 0 ? (
                            paymentsData.payments.map((payment: Payment) => (
                              <Card key={payment.id} className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start space-x-3">
                                    <div className={`p-2 rounded-full ${payment.isRefund ? 'bg-red-100' : 'bg-green-100'}`}>
                                      {payment.isRefund ? (
                                        <AlertCircle className="h-5 w-5 text-red-600" />
                                      ) : (
                                        <CreditCard className="h-5 w-5 text-green-600" />
                                      )}
                                    </div>
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <p className={`font-semibold ${payment.isRefund ? 'text-red-600' : 'text-green-600'}`}>
                                          {payment.isRefund ? '-' : '+'}₹{payment.amount.toLocaleString('en-IN')}
                                        </p>
                                        <Badge variant="outline" className="text-xs">
                                          {PAYMENT_METHOD_LABELS[payment.paymentMethod]}
                                        </Badge>
                                        <Badge
                                          className={`text-xs ${
                                            payment.status === 'VERIFIED' ? 'bg-green-100 text-green-700' :
                                            payment.status === 'RECEIVED' ? 'bg-blue-100 text-blue-700' :
                                            payment.status === 'REFUNDED' ? 'bg-red-100 text-red-700' :
                                            'bg-gray-100 text-gray-700'
                                          }`}
                                        >
                                          {payment.status}
                                        </Badge>
                                      </div>
                                      <p className="text-sm text-gray-500 mt-1">
                                        {new Date(payment.paymentDate).toLocaleDateString('en-IN', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                        {payment.invoiceNumber && ` • Invoice: ${payment.invoiceNumber}`}
                                        {payment.receiptNumber && ` • Receipt: ${payment.receiptNumber}`}
                                      </p>
                                      {payment.notes && (
                                        <p className="text-sm text-gray-600 mt-1">{payment.notes}</p>
                                      )}
                                      {payment.isRefund && payment.refundReason && (
                                        <p className="text-sm text-red-600 mt-1">Reason: {payment.refundReason}</p>
                                      )}
                                      <p className="text-xs text-gray-400 mt-1">
                                        Recorded by {payment.recordedBy?.name || 'System'}
                                        {payment.verifiedBy && ` • Verified by ${payment.verifiedBy.name}`}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                              <p>No payments recorded yet</p>
                              <p className="text-sm">Click "Add Payment" to record your first payment</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'interaction' && (
                    <motion.div
                      key="interaction"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      <Card className="p-6">
                        <div className="flex items-center space-x-2 mb-6">
                          <Phone className="h-5 w-5 text-purple-600" />
                          <h3 className="font-semibold text-gray-900">Log Call / Interaction</h3>
                        </div>

                        <div className="space-y-4">
                          {/* Sub-Disposition */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Call Outcome <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={interaction.subDisposition}
                              onChange={(e) => setInteraction(prev => ({ ...prev, subDisposition: e.target.value }))}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="">Select outcome...</option>
                              {SUB_DISPOSITIONS.map(d => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </select>
                          </div>

                          {/* Follow-up date — shown when relevant */}
                          {interaction.subDisposition && NEEDS_FOLLOWUP.includes(interaction.subDisposition) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Follow-up Date
                              </label>
                              <input
                                type="date"
                                value={interaction.followUpDate}
                                onChange={(e) => setInteraction(prev => ({ ...prev, followUpDate: e.target.value }))}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                            </div>
                          )}

                          {/* Note */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Note (optional)
                            </label>
                            <Textarea
                              value={interaction.followUpNote}
                              onChange={(e) => setInteraction(prev => ({ ...prev, followUpNote: e.target.value }))}
                              placeholder="What was discussed..."
                              rows={3}
                              className="text-sm"
                            />
                          </div>

                          {/* Create task checkbox */}
                          {interaction.subDisposition && NEEDS_FOLLOWUP.includes(interaction.subDisposition) && (
                            <div className="space-y-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={interaction.createTask}
                                  onChange={(e) => setInteraction(prev => ({ ...prev, createTask: e.target.checked }))}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm font-medium text-gray-700">Create follow-up task</span>
                              </label>

                              {interaction.createTask && (
                                <div className="pl-6 space-y-2">
                                  <input
                                    type="text"
                                    value={interaction.taskTitle}
                                    onChange={(e) => setInteraction(prev => ({ ...prev, taskTitle: e.target.value }))}
                                    placeholder="Task title (e.g. Call back John)"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  />
                                  <select
                                    value={interaction.taskPriority}
                                    onChange={(e) => setInteraction(prev => ({ ...prev, taskPriority: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  >
                                    <option value="LOW">Low priority</option>
                                    <option value="MEDIUM">Medium priority</option>
                                    <option value="HIGH">High priority</option>
                                    <option value="URGENT">Urgent</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          )}

                          <Button
                            onClick={async () => {
                              if (!lead?.id) {
                                toast({ title: 'No lead selected', description: 'Cannot log interaction without a valid lead.', variant: 'destructive' });
                                return;
                              }
                              if (!interaction.subDisposition) {
                                toast({ title: 'Select a call outcome', variant: 'destructive' });
                                return;
                              }
                              setIsLoggingInteraction(true);
                              try {
                                const token = localStorage.getItem('authToken') || localStorage.getItem('token');
                                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                                const body: Record<string, unknown> = {
                                  subDisposition: interaction.subDisposition,
                                  followUpNote: interaction.followUpNote,
                                  createTask: interaction.createTask,
                                };
                                if (interaction.followUpDate) body.followUpDate = interaction.followUpDate;
                                if (interaction.createTask && interaction.taskTitle) {
                                  body.taskTitle = interaction.taskTitle;
                                  body.taskPriority = interaction.taskPriority;
                                }
                                const res = await fetch(`${API_URL}/leads/${lead.id}/disposition`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify(body),
                                });
                                const data = await res.json();
                                if (!res.ok || !data.success) throw new Error(data.message || 'Failed');
                                toast({ title: 'Interaction logged', description: `${interaction.subDisposition}${data.data?.task ? ' • Follow-up task created' : ''}` });
                                setInteraction({ subDisposition: '', followUpDate: '', followUpNote: '', createTask: false, taskTitle: '', taskPriority: 'MEDIUM' });
                                queryClient.invalidateQueries({ queryKey: ['lead-detail', lead?.id] });
                                queryClient.invalidateQueries({ queryKey: ['leads'] });
                                queryClient.invalidateQueries({ queryKey: ['disposition-history', lead?.id] });
                                if (data.data?.lead) onLeadUpdate(data.data.lead);
                              } catch (err: unknown) {
                                const msg = err instanceof Error ? err.message : 'Failed to log interaction';
                                toast({ title: 'Error', description: msg, variant: 'destructive' });
                              } finally {
                                setIsLoggingInteraction(false);
                              }
                            }}
                            disabled={isLoggingInteraction || !interaction.subDisposition}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            {isLoggingInteraction ? 'Logging...' : 'Log Interaction'}
                          </Button>
                        </div>
                      </Card>

                      {/* Show current sub-disposition if set */}
                      {(lead?.subDisposition || lead?.followUpDate) && (
                        <Card className="p-4 bg-purple-50 border-purple-200">
                          <h4 className="text-sm font-medium text-purple-800 mb-2">Last Logged</h4>
                          <div className="space-y-1 text-sm text-purple-700">
                            {lead?.subDisposition && <p>Outcome: <strong>{lead.subDisposition}</strong></p>}
                            {lead?.followUpDate && <p>Follow-up: <strong>{new Date(lead.followUpDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</strong></p>}
                          </div>
                        </Card>
                      )}

                      {/* Disposition History */}
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          Call History
                        </h4>
                        <DispositionHistoryTimeline leadId={lead?.id || ''} isVisible={activeTab === 'interaction'} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      )}

      {/* Reassign Lead Modal */}
      {lead && (
        <ReassignLeadModal
          key="reassign-modal"
          open={showReassignModal}
          onClose={() => setShowReassignModal(false)}
          lead={lead}
          onReassign={handleReassign}
        />
      )}

      {/* Edit Lead Modal */}
      {lead && (
        <EditLeadModal
          key="edit-modal"
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          lead={displayLead}
          onLeadUpdated={onLeadUpdate}
        />
      )}
    </AnimatePresence>
  );
}; 